import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { WidgetSize } from '../../../types/Dashboard';
import { useAuth } from '../../../contexts/AuthContext';
import { firebaseService } from '../../../services/firebaseService';

interface StatCardWidgetProps {
  config: {
    statType?: string;
  };
  size: WidgetSize;
}

const StatCardWidget: React.FC<StatCardWidgetProps> = ({ config, size }) => {
  const { userProfile } = useAuth();
  const [value, setValue] = useState<string | number>('0');
  const [label, setLabel] = useState('');
  const [change, setChange] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.statType, userProfile?.empresaCnpj]);

  const loadStatData = async () => {
    if (!userProfile?.empresaCnpj || !config.statType) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const hoje = new Date();
      const mesAtual = hoje.getMonth();
      const anoAtual = hoje.getFullYear();
      const mensalidades = await firebaseService.getAllMensalidades(userProfile.empresaCnpj);
      const alunos = await firebaseService.getAlunos(userProfile.empresaCnpj);

      switch (config.statType) {
        case 'taxa-inadimplencia':
          setLabel('Taxa de Inadimplência');
          const mensalidadesMes = mensalidades.filter(m => {
            const dataVencimento = new Date(m.dataVencimento);
            return dataVencimento.getMonth() === mesAtual && 
                   dataVencimento.getFullYear() === anoAtual;
          });
          const vencidas = mensalidadesMes.filter(m => {
            if (m.dataPagamento && m.dataPagamento !== '') return false;
            return new Date(m.dataVencimento) < hoje;
          });
          const taxa = mensalidadesMes.length > 0 
            ? (vencidas.length / mensalidadesMes.length) * 100 
            : 0;
          setValue(`${taxa.toFixed(1)}%`);
          break;
        case 'media-mensalidade':
          setLabel('Média de Mensalidade');
          const valores = mensalidades.map(m => m.valor).filter(v => v > 0);
          const media = valores.length > 0 
            ? valores.reduce((sum, v) => sum + v, 0) / valores.length 
            : 0;
          setValue(`R$ ${media.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
          break;
        case 'crescimento-mensal':
          setLabel('Crescimento Mensal');
          const mesAnterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
          const alunosMesAtual = alunos.filter(a => {
            const dataCriacao = new Date(a.createDate);
            return dataCriacao.getMonth() === mesAtual && 
                   dataCriacao.getFullYear() === anoAtual;
          }).length;
          const alunosMesAnterior = alunos.filter(a => {
            const dataCriacao = new Date(a.createDate);
            return dataCriacao.getMonth() === mesAnterior.getMonth() && 
                   dataCriacao.getFullYear() === mesAnterior.getFullYear();
          }).length;
          const crescimento = alunosMesAnterior > 0 
            ? ((alunosMesAtual - alunosMesAnterior) / alunosMesAnterior) * 100 
            : alunosMesAtual > 0 ? 100 : 0;
          setValue(`${crescimento >= 0 ? '+' : ''}${crescimento.toFixed(1)}%`);
          setChange(crescimento);
          break;
        default:
          setLabel('Estatística');
          setValue('0');
      }
    } catch (error) {
      console.error('Erro ao carregar estatística:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-300"></div>
      </div>
    );
  }

  const textSize = size === 'small' ? 'text-2xl' : size === 'medium' ? 'text-3xl' : 'text-4xl';

  return (
    <div className="flex flex-col h-full justify-center min-h-0">
      <p className="text-sm font-medium text-gray-500 mb-2">{label}</p>
      <div className="flex items-baseline gap-2">
        <p className={`font-bold ${textSize} text-gray-900`}>{value}</p>
        {change !== null && (
          <div className={`flex items-center gap-1 ${change >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
            {change > 0 && <TrendingUp className="h-4 w-4" />}
            {change < 0 && <TrendingDown className="h-4 w-4" />}
            {change === 0 && <Minus className="h-4 w-4" />}
          </div>
        )}
      </div>
    </div>
  );
};

export default StatCardWidget;

